import React, { useEffect, useMemo, useState } from 'react'
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '@tabler/icons-react'
import { InputField } from '../InputField'
import { formatTimePickerValue } from '../TimePicker/TimePicker.utils'
import type { CalendarProps } from './Calendar.types'
import {
  CALENDAR_MODE_LABELS,
  getCalendarDays,
  getYearPageStart,
  isBetweenDays,
  isCalendarDateDisabled,
  isSameDay,
  MONTH_SHORT_LABELS,
  WEEKDAY_SHORT_LABELS,
} from './Calendar.utils'
import './Calendar.css'

type CalendarPanel = 'days' | 'months' | 'years'

const createMarch2026 = (): Date => new Date(2026, 2, 1)

const panelForMode = (mode: CalendarProps['mode']): CalendarPanel => {
  if (mode === 'month') {
    return 'months'
  }
  if (mode === 'year') {
    return 'years'
  }
  return 'days'
}

const Calendar: React.FC<CalendarProps> = ({
  mode = 'date',
  value = null,
  rangeValue = { start: null, end: null },
  month,
  year,
  viewDate,
  defaultViewDate = createMarch2026(),
  onChange,
  onRangeChange,
  onMonthChange,
  onYearChange,
  onViewDateChange,
  timeValue,
  onTimeChange,
  allowedWeekdays,
  isDateDisabled,
  darkMode = false,
  className = '',
  style,
}) => {
  const [internalViewDate, setInternalViewDate] = useState(defaultViewDate)
  const [internalTimeValue, setInternalTimeValue] = useState('')
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev' | null>(
    null,
  )
  const [panelAnimKey, setPanelAnimKey] = useState(0)
  const [panel, setPanel] = useState<CalendarPanel>(() => panelForMode(mode))
  const currentViewDate = viewDate ?? internalViewDate
  const resolvedTimeValue =
    timeValue !== undefined ? timeValue : internalTimeValue

  useEffect(() => {
    setPanel(panelForMode(mode))
  }, [mode])

  const setViewDate = (nextDate: Date, direction?: 'next' | 'prev'): void => {
    if (direction) {
      setSlideDirection(direction)
      setPanelAnimKey((key) => key + 1)
    }
    if (viewDate == null) {
      setInternalViewDate(nextDate)
    }
    onViewDateChange?.(nextDate)
  }

  const handleTimeSelect = (time: Date): void => {
    const formatted = formatTimePickerValue(time)
    if (timeValue === undefined) {
      setInternalTimeValue(formatted)
    }
    onTimeChange?.(time)
  }

  const handleTimeClear = (): void => {
    if (timeValue === undefined) {
      setInternalTimeValue('')
    }
    onTimeChange?.(null)
  }

  const viewYear = currentViewDate.getFullYear()
  const viewMonth = currentViewDate.getMonth()
  const selectedMonth = month ?? viewMonth
  const selectedYear = year ?? viewYear

  const days = useMemo(
    () => getCalendarDays(currentViewDate),
    [currentViewDate],
  )

  const yearPageStart = getYearPageStart(
    panel === 'years' || mode === 'year' ? selectedYear : viewYear,
  )
  const yearOptions = useMemo(
    () => Array.from({ length: 12 }, (_, index) => yearPageStart + index),
    [yearPageStart],
  )

  const rangeStart = rangeValue.start
  const rangeEnd = rangeValue.end
  const hasRange = rangeStart != null && rangeEnd != null
  const isDateMode = mode === 'date' || mode === 'range' || mode === 'datetime'
  const showDays = isDateMode && panel === 'days'
  const showMonths = panel === 'months'
  const showYears = panel === 'years'
  const showMonthNav = showDays
  const showDoubleChevrons = showDays

  const shiftMonth = (delta: number): void => {
    setViewDate(
      new Date(viewYear, viewMonth + delta, 1),
      delta > 0 ? 'next' : 'prev',
    )
  }

  const shiftYear = (delta: number): void => {
    setViewDate(
      new Date(viewYear + delta, viewMonth, 1),
      delta > 0 ? 'next' : 'prev',
    )
  }

  const shiftYearPage = (delta: number): void => {
    const baseYear =
      mode === 'year' || panel === 'years' ? selectedYear : viewYear
    const nextYear = baseYear + delta
    onYearChange?.(nextYear)
    setViewDate(new Date(nextYear, viewMonth, 1), delta > 0 ? 'next' : 'prev')
  }

  const isDayDisabled = (date: Date): boolean =>
    isCalendarDateDisabled(date, { allowedWeekdays, isDateDisabled })

  const isWeekdayColumnDisabled = (weekdayIndex: number): boolean =>
    Boolean(
      allowedWeekdays?.length &&
        !allowedWeekdays.includes(
          weekdayIndex as (typeof allowedWeekdays)[number],
        ),
    )

  const handleDayClick = (date: Date): void => {
    if (isDayDisabled(date)) {
      return
    }

    if (mode === 'range') {
      if (rangeStart == null || (rangeStart != null && rangeEnd != null)) {
        onRangeChange?.({ start: date, end: null })
        return
      }

      if (isSameDay(date, rangeStart)) {
        onRangeChange?.({ start: date, end: date })
        return
      }

      if (date < rangeStart) {
        onRangeChange?.({ start: date, end: rangeStart })
        return
      }

      onRangeChange?.({ start: rangeStart, end: date })
      return
    }

    onChange?.(date)
    if (date.getMonth() !== viewMonth || date.getFullYear() !== viewYear) {
      const next = new Date(date.getFullYear(), date.getMonth(), 1)
      const direction =
        next.getTime() > new Date(viewYear, viewMonth, 1).getTime()
          ? 'next'
          : 'prev'
      setViewDate(next, direction)
    }
  }

  const handleMonthClick = (monthIndex: number): void => {
    const yearForMonth = viewYear
    onMonthChange?.(monthIndex, yearForMonth)
    const next = new Date(yearForMonth, monthIndex, 1)
    if (monthIndex === viewMonth && yearForMonth === viewYear) {
      setViewDate(next)
    } else {
      setViewDate(next, monthIndex > viewMonth ? 'next' : 'prev')
    }
    if (isDateMode) {
      setPanel('days')
    }
  }

  const handleYearClick = (nextYear: number): void => {
    onYearChange?.(nextYear)
    const next = new Date(nextYear, viewMonth, 1)
    if (nextYear === viewYear) {
      setViewDate(next)
    } else {
      setViewDate(next, nextYear > viewYear ? 'next' : 'prev')
    }
    if (mode === 'year') {
      return
    }
    // After picking a year, land on month picker (date modes + month mode)
    setPanel('months')
  }

  const openMonthsPanel = (): void => {
    setPanel('months')
  }

  const openYearsPanel = (): void => {
    setPanel('years')
  }

  const panelAnimClass =
    slideDirection === 'next'
      ? ' ds-calendar__panel--slide-next'
      : slideDirection === 'prev'
        ? ' ds-calendar__panel--slide-prev'
        : ''

  const highlightedMonth = mode === 'month' ? selectedMonth : viewMonth
  const highlightedYear = mode === 'year' ? selectedYear : viewYear

  const titleKey = showYears
    ? 'year'
    : showMonths
      ? `month-${highlightedYear}`
      : `day-${viewMonth}-${viewYear}`

  const handlePrevNav = (): void => {
    if (showYears) {
      shiftYearPage(-12)
      return
    }
    if (showMonths) {
      shiftYear(-1)
      return
    }
    shiftYear(-1)
  }

  const handleNextNav = (): void => {
    if (showYears) {
      shiftYearPage(12)
      return
    }
    if (showMonths) {
      shiftYear(1)
      return
    }
    shiftYear(1)
  }

  const getDayClassName = (cell: (typeof days)[number]): string => {
    const classes = ['ds-calendar__day']

    if (!cell.isCurrentMonth) {
      classes.push('ds-calendar__day--outside')
    } else if (cell.isSunday) {
      classes.push('ds-calendar__day--sunday')
    } else if (cell.isSaturday) {
      classes.push('ds-calendar__day--saturday')
    }

    if (
      (mode === 'date' || mode === 'datetime') &&
      value &&
      isSameDay(cell.date, value)
    ) {
      classes.push('ds-calendar__day--selected')
    }

    if (mode === 'range' && rangeStart) {
      const isStart = isSameDay(cell.date, rangeStart)
      const isEnd = rangeEnd != null && isSameDay(cell.date, rangeEnd)

      if (isStart && isEnd) {
        classes.push('ds-calendar__day--selected')
      } else if (isStart) {
        classes.push('ds-calendar__day--range-start')
      } else if (isEnd) {
        classes.push('ds-calendar__day--range-end')
      } else if (
        hasRange &&
        isBetweenDays(cell.date, rangeStart, rangeEnd as Date)
      ) {
        classes.push('ds-calendar__day--range-middle')
      }
    }

    if (isDayDisabled(cell.date)) {
      classes.push('ds-calendar__day--disabled')
    }

    return classes.join(' ')
  }

  return (
    <div
      className={`ds-calendar ds-calendar--${mode}${
        darkMode ? ' ds-calendar--dark' : ''
      } ${className}`.trim()}
      style={style}
    >
      <div className="ds-calendar__header">
        <button
          type="button"
          className="ds-calendar__nav-button"
          aria-label={showDoubleChevrons ? 'Previous year' : 'Previous'}
          onClick={handlePrevNav}
        >
          {showDoubleChevrons ? (
            <IconChevronsLeft size={16} stroke={2} />
          ) : (
            <IconChevronLeft size={16} stroke={2} />
          )}
        </button>

        {showMonthNav ? (
          <button
            type="button"
            className="ds-calendar__nav-button"
            aria-label="Previous month"
            onClick={() => shiftMonth(-1)}
          >
            <IconChevronLeft size={16} stroke={2} />
          </button>
        ) : null}

        <div
          key={`title-${panelAnimKey}-${titleKey}`}
          className={`ds-calendar__title${
            slideDirection ? ' ds-calendar__title--fade' : ''
          }`}
        >
          {showYears ? (
            <span>{CALENDAR_MODE_LABELS.year}</span>
          ) : showMonths ? (
            <button
              type="button"
              className="ds-calendar__title-trigger"
              aria-label={`Select year, currently ${highlightedYear}`}
              onClick={openYearsPanel}
            >
              {highlightedYear}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="ds-calendar__title-trigger"
                aria-label={`Select month, currently ${MONTH_SHORT_LABELS[viewMonth]}`}
                onClick={openMonthsPanel}
              >
                {MONTH_SHORT_LABELS[viewMonth]}
              </button>
              <button
                type="button"
                className="ds-calendar__title-trigger"
                aria-label={`Select year, currently ${viewYear}`}
                onClick={openYearsPanel}
              >
                {viewYear}
              </button>
            </>
          )}
        </div>

        {showMonthNav ? (
          <button
            type="button"
            className="ds-calendar__nav-button"
            aria-label="Next month"
            onClick={() => shiftMonth(1)}
          >
            <IconChevronRight size={16} stroke={2} />
          </button>
        ) : null}

        <button
          type="button"
          className="ds-calendar__nav-button"
          aria-label={showDoubleChevrons ? 'Next year' : 'Next'}
          onClick={handleNextNav}
        >
          {showDoubleChevrons ? (
            <IconChevronsRight size={16} stroke={2} />
          ) : (
            <IconChevronRight size={16} stroke={2} />
          )}
        </button>
      </div>

      {showDays ? (
        <>
          <div className="ds-calendar__weekdays">
            {WEEKDAY_SHORT_LABELS.map((label, index) => (
              <span
                key={label}
                className={`ds-calendar__weekday${
                  index === 0
                    ? ' ds-calendar__weekday--sunday'
                    : index === 6
                      ? ' ds-calendar__weekday--saturday'
                      : ''
                }${
                  isWeekdayColumnDisabled(index)
                    ? ' ds-calendar__weekday--disabled'
                    : ''
                }`}
              >
                {label}
              </span>
            ))}
          </div>

          <div
            key={`days-${panelAnimKey}`}
            className={`ds-calendar__grid ds-calendar__grid--days${panelAnimClass}`}
          >
            {days.map((cell) => {
              const disabled = isDayDisabled(cell.date)

              return (
                <button
                  key={cell.date.toISOString()}
                  type="button"
                  className={getDayClassName(cell)}
                  onClick={() => handleDayClick(cell.date)}
                  disabled={disabled}
                  aria-disabled={disabled}
                  aria-pressed={
                    mode === 'date' || mode === 'datetime'
                      ? Boolean(value && isSameDay(cell.date, value))
                      : Boolean(
                          rangeStart && isSameDay(cell.date, rangeStart),
                        ) || Boolean(rangeEnd && isSameDay(cell.date, rangeEnd))
                  }
                  aria-label={cell.date.toDateString()}
                >
                  {cell.day}
                </button>
              )
            })}
          </div>
        </>
      ) : null}

      {showMonths ? (
        <div
          key={`months-${panelAnimKey}`}
          className={`ds-calendar__grid ds-calendar__grid--months${panelAnimClass}`}
        >
          {MONTH_SHORT_LABELS.map((label, index) => (
            <button
              key={label}
              type="button"
              className={`ds-calendar__month${
                index === highlightedMonth
                  ? ' ds-calendar__month--selected'
                  : ''
              }`}
              onClick={() => handleMonthClick(index)}
              aria-pressed={index === highlightedMonth}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {showYears ? (
        <div
          key={`years-${panelAnimKey}`}
          className={`ds-calendar__grid ds-calendar__grid--years${panelAnimClass}`}
        >
          {yearOptions.map((optionYear) => (
            <button
              key={optionYear}
              type="button"
              className={`ds-calendar__year${
                optionYear === highlightedYear
                  ? ' ds-calendar__year--selected'
                  : ''
              }`}
              onClick={() => handleYearClick(optionYear)}
              aria-pressed={optionYear === highlightedYear}
            >
              {optionYear}
            </button>
          ))}
        </div>
      ) : null}

      {mode === 'datetime' && showDays ? (
        <div className="ds-calendar__time">
          <InputField
            variant="time"
            label="Time"
            placeholder="Select time"
            value={resolvedTimeValue}
            onTimeChange={handleTimeSelect}
            onClear={handleTimeClear}
            darkMode={darkMode}
          />
        </div>
      ) : null}
    </div>
  )
}

export default Calendar
